import React, { MouseEventHandler, createContext, useContext, useState, type JSX } from 'react';
import { IconButton } from '@tv2/ui-system/components/IconButton';
import { LaptopAlertNext } from '@tv2/ui-system/icons/next/general/LaptopAlertNext';
import { CloseBasicNext } from '@tv2/ui-system/icons/next/navigation/CloseBasicNext';
import { usePathOrDefault } from '@tv2/lib-offspring/hooks/usePath';
import { FlagBasicNext } from '@tv2/ui-system/icons/next/sport/FlagBasicNext';
import { MergedModification, useModificationContext } from './ModificationsProvider';
import { GridOverlay } from './GridOverlay';
import { useAdmin, PanelData } from './AdminProvider';
import { PromoPageAdminPanel } from './Content/PromoAdminPanel';
import { FooterAdminPanel } from './Footer/FooterAdminPanel';
import { useLoadWysiwyg } from './components/WysiwygEditor/useLoadWysiswyg';
import { FeatureFlagsPanel } from './feature-flags/FeatureFlagsPanel';

const GridContext = createContext<{ showGrid: boolean; toggleGrid: () => void }>({
  showGrid: false,
  toggleGrid: () => {},
});

export const PCSPanels = [
  {
    panel: <AdminPanel />,
    buttonData: {
      icon: LaptopAlertNext,
    },
  },
  {
    panel: <FeatureFlagsPanel />,
    buttonData: {
      icon: FlagBasicNext,
    },
  } satisfies PanelData,
];

export default function AdminPanels() {
  const [minimized, setMinimized] = useState(-1);
  const [showGrid, setShowGrid] = useState(false);
  const { panels } = useAdmin();
  useLoadWysiwyg();

  const { buttons, panelComponents } = panels.reduce<{
    buttons: PanelData['buttonData'][];
    panelComponents: PanelData['panel'][];
  }>(
    (acc, c) => {
      acc.buttons.push(c.buttonData);
      acc.panelComponents.push(c.panel);
      return acc;
    },
    { buttons: [], panelComponents: [] }
  );

  return (
    <GridContext.Provider value={{ showGrid, toggleGrid: () => setShowGrid(x => !x) }}>
      {showGrid ? <GridOverlay /> : null}

      {minimized !== -1 ? panelComponents[minimized] : null}

      <div className="fixed top-2 right-2 z-[70] flex flex-col space-y-4">
        {buttons.map((btn, index) => (
          <IconButton
            key={`btn-${index}`}
            invertIcon
            onClick={() => setMinimized(x => (x === index ? -1 : index))}
            icon={minimized !== index ? btn.icon : CloseBasicNext}
            color="primary-red"
          />
        ))}
      </div>
    </GridContext.Provider>
  );
}

function getModificationAreas(area: MergedModification | null, path: string) {
  if (!area) return [];
  const modificationAreas: JSX.Element[] = [];

  switch (area.name) {
    case 'detailsPage':
      modificationAreas.push(<PromoPageAdminPanel modificationType={area} path={path} />);
      break;
  }

  if (area.footer && area.footer.categories.length > 0) {
    modificationAreas.push(<FooterAdminPanel footerData={area.footer} />);
  }

  return modificationAreas;
}

export function AdminPanel() {
  const { showGrid, toggleGrid } = useContext(GridContext);
  const path = usePathOrDefault();
  const { emulateLogout } = useAdmin();
  const { modifications } = useModificationContext();
  const modificationAreas = getModificationAreas(modifications, path);

  return (
    <PanelContainer>
      <div className="py-2 flex-grow-0">
        <label htmlFor="admin-emulate-logout" className="mr-2">
          Vis som logget ud:
        </label>
        <input
          id="admin-emulate-logout"
          onChange={() => {
            emulateLogout(x => !x);
          }}
          type="checkbox"
        />
      </div>
      {modificationAreas.length > 0 &&
        modificationAreas.map((modificationArea, index) => (
          <div key={index}>
            <div className="flex-grow-0 my-2">{modificationArea}</div>
            <hr />
          </div>
        ))}
      <div className="text-midnight-300 py-2 flex-grow-0 justify-end">
        <CurrentBreakpoint />
        <label htmlFor="admin-show-grid" className="mr-2">
          Vis grid:
        </label>
        <input
          id="admin-show-grid"
          checked={showGrid}
          onChange={() => {
            toggleGrid();
          }}
          type="checkbox"
        />
      </div>
    </PanelContainer>
  );
}

export function PanelContainer({ children }: { children: React.ReactNode }) {
  const { width, startResize } = useResize();

  return (
    <div
      className="fixed bottom-0 top-0 right-0 opacity-90 shadow-xl bg-midnight text-midnight-300 overflow-scroll z-50"
      style={{ width }}
    >
      <div
        className="h-full absolute left-0 w-1 bg-midnight-500 cursor-col-resize select-none"
        onMouseDown={startResize}
      ></div>
      <div className="pt-18 px-4 flex flex-col h-full">{children}</div>
    </div>
  );
}

function CurrentBreakpoint() {
  return (
    <div className="text-subheadline-xs text-white">
      <div className="360:hidden"> {'Breakpoint: < 360'} </div>
      <div className="hidden 360:block 576:hidden"> {'Breakpoint: 360 < 576'} </div>
      <div className="hidden 576:block 768:hidden"> {'Breakpoint: 576 < 768'} </div>
      <div className="hidden 768:block 992:hidden"> {'Breakpoint: 768 < 992'} </div>
      <div className="hidden 992:block 1200:hidden"> {'Breakpoint: 992 < 1200'} </div>
      <div className="hidden 1200:block 1440:hidden"> {'Breakpoint: 1200 < 1440'} </div>
      <div className="hidden 1440:block 1920:hidden"> {'Breakpoint: 1440 < 1920'} </div>
      <div className="hidden 1920:block"> {'Breakpoint:  1920 <'} </div>
    </div>
  );
}

function useResize() {
  const [width, setWidth] = useState(400);

  const startResize: MouseEventHandler = e => {
    let pos = e.pageX;
    const resize = (ev: MouseEvent) => {
      const res = ev.pageX - pos;
      pos = ev.pageX;
      setWidth(w => Math.max(200, w - res));
    };
    const endResize = () => {
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', endResize);
    };
    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', endResize);
  };

  return {
    width,
    startResize,
  };
}
